/**
 * Nass Lessons Marquee — smooth, swipeable auto-scrolling carousel.
 *
 * The track is moved with `transform: translate3d(-offset,0,0)` instead of
 * writing `scrollLeft` every frame. Mobile browsers snap scrollLeft to whole
 * pixels, so a slow auto-scroll (~0.75px/frame) advanced 1,1,0,1,1,0 px — the
 * visible stutter the user reported. translate3d is GPU-composited and honours
 * sub-pixel offsets, so the motion is smooth; it also avoids fighting iOS
 * momentum scrolling.
 *
 * Swipe is handled with pointer events (touch + mouse) since the viewport no
 * longer scrolls natively. `touch-action: pan-y` on the viewport keeps vertical
 * page scrolling native — a mostly-vertical gesture becomes a page scroll (the
 * browser fires pointercancel, which just ends the drag). The track renders the
 * cards twice, so wrapping by half its width is seamless. Auto-advance pauses
 * during drag / hover / horizontal wheel and resumes shortly after release.
 * Respects prefers-reduced-motion (no auto-advance; drag still works).
 */
(function () {
  var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  document.querySelectorAll('[data-lm-viewport]').forEach(function (vp) {
    var track = vp.querySelector('[data-lm-track]');
    if (!track) return;

    var SPEED = 42;          // px / second
    var offset = 0;          // current translate distance (px, 0..half)
    var dragging = false;
    var paused = false;      // temporary pause after interaction
    var offscreen = false;
    var resumeTimer = null;
    var last = 0;
    var startX = 0, startOffset = 0, pointerId = null;

    function half() { return (track.scrollWidth / 2) || 0; }

    // Keep offset within [0, half) so translate never runs off the duplicated set.
    function norm(x) {
      var h = half();
      if (h <= 0) return 0;
      x %= h;
      if (x < 0) x += h;
      return x;
    }

    function apply() {
      track.style.transform = 'translate3d(' + (-offset) + 'px, 0, 0)';
    }

    function frame(ts) {
      if (!last) last = ts;
      var dt = (ts - last) / 1000;
      last = ts;
      if (dt > 0.05) dt = 0.05; // clamp big gaps (tab switch / stall) so it doesn't jump
      if (!dragging && !paused && !offscreen && !reduce) {
        offset = norm(offset + SPEED * dt);
        apply();
      }
      requestAnimationFrame(frame);
    }

    function resumeSoon() {
      if (resumeTimer) clearTimeout(resumeTimer);
      resumeTimer = setTimeout(function () { paused = false; last = 0; }, 1600);
    }
    function pauseNow() {
      paused = true;
      if (resumeTimer) { clearTimeout(resumeTimer); resumeTimer = null; }
    }

    // Pointer drag — touch + mouse swipe (viewport no longer scrolls natively).
    vp.addEventListener('pointerdown', function (e) {
      dragging = true;
      pauseNow();
      startX = e.clientX;
      startOffset = offset;
      pointerId = e.pointerId;
      try { vp.setPointerCapture(e.pointerId); } catch (err) {}
    }, { passive: true });

    vp.addEventListener('pointermove', function (e) {
      if (!dragging) return;
      offset = norm(startOffset - (e.clientX - startX));
      apply();
    }, { passive: true });

    function endDrag() {
      if (!dragging) return;
      dragging = false;
      try { if (pointerId != null) vp.releasePointerCapture(pointerId); } catch (err) {}
      pointerId = null;
      resumeSoon();
    }
    vp.addEventListener('pointerup', endDrag, { passive: true });
    vp.addEventListener('pointercancel', endDrag, { passive: true });

    // Desktop: pause on hover, scrub with horizontal wheel.
    vp.addEventListener('mouseenter', pauseNow, { passive: true });
    vp.addEventListener('mouseleave', resumeSoon, { passive: true });
    vp.addEventListener('wheel', function (e) {
      var dx = Math.abs(e.deltaX) > Math.abs(e.deltaY) ? e.deltaX : 0;
      if (!dx) return;
      pauseNow();
      offset = norm(offset + dx);
      apply();
      resumeSoon();
    }, { passive: true });

    // Don't auto-advance while off-screen.
    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (entries) {
        entries.forEach(function (e) { offscreen = !e.isIntersecting; if (!offscreen) last = 0; });
      }, { threshold: 0 }).observe(vp);
    }

    apply();
    requestAnimationFrame(frame);
  });
})();
